import { useEffect, useState } from "react";
import { PageHeader, Empty } from "@/components/UIBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", real_number: "", proxy_number: "" });

  const load = async () => {
    try {
      setUsers(await api.listUsers());
    } catch {/* ignore */}
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.name || !form.real_number || !form.proxy_number) {
      return toast.error("All fields required");
    }
    try {
      await api.createUser(form);
      toast.success(`User ${form.name} registered`);
      setForm({ name: "", real_number: "", proxy_number: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.name}? Active mappings will be burned.`)) return;
    try {
      await api.deleteUser(u.real_number);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Users" subtitle={`// ${users.length} registered`} testid="users-header" />

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div>
          {users.length === 0 ? (
            <Empty testid="users-empty">No users yet — register one →</Empty>
          ) : (
            <div className="border border-[#1f1f23] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1f1f23] bg-[#0e0e10]">
                    {["Name", "Real Number", "Proxy Number", "Joined", ""].map((h) => (
                      <th
                        key={h}
                        className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500 px-4 py-3"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      data-testid={`user-row-${u.real_number}`}
                      className="border-b border-[#18181b] hover:bg-[#0e0e10]"
                    >
                      <td className="px-4 py-3 text-white">{u.name}</td>
                      <td className="px-4 py-3 font-mono">{u.real_number}</td>
                      <td className="px-4 py-3 font-mono text-amber-400">{u.proxy_number}</td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => del(u)}
                          data-testid={`user-delete-${u.real_number}`}
                          className="text-zinc-600 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <aside className="border border-[#1f1f23] bg-[#0e0e10] p-5">
          <h3 className="font-heading text-lg mb-1">Register User</h3>
          <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mb-5">
            Bind real number ↔ proxy
          </p>
          <div className="space-y-3">
            <Field
              label="Name"
              testid="user-name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              placeholder="Alex Chen"
            />
            <Field
              label="Real Number"
              testid="user-real"
              value={form.real_number}
              onChange={(v) => setForm({ ...form, real_number: v })}
              placeholder="+15550010001"
              mono
            />
            <Field
              label="Proxy Number"
              testid="user-proxy"
              value={form.proxy_number}
              onChange={(v) => setForm({ ...form, proxy_number: v })}
              placeholder="+15558880001"
              mono
            />
          </div>
          <Button
            onClick={create}
            data-testid="user-create-btn"
            className="w-full mt-5 bg-white text-black hover:bg-zinc-200"
          >
            Register
          </Button>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, mono }) {
  return (
    <div>
      <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        {label}
      </Label>
      <Input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`mt-1 bg-black border-[#27272a] ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}
